import { Box, styled, Typography, useTheme } from '@mui/material';
import { Card } from 'components/commons';
import SecuredImage from 'components/hooks/SecuredImage';
import { useClusterDbSocketContext } from 'components/hooks/useClusterDbSocketContext';
import { useHeaderContext } from 'components/providers';
import { API_PATHS } from 'constant/api';
import { PAGE_LIST } from 'constant/pages';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  convertDateUTCToLocal,
  DatePattern,
  getKeyFromLocalStorage,
} from 'utils/constant';
import { getVisits } from 'utils/fetch';
import ja from 'utils/ja';

const TypographyCustom = styled(Typography)(() => ({
  fontWeight: '400',
  lineHeight: '48px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
}));

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { date } = useHeaderContext();
  const { eventId, reconnect, isConnected } = useClusterDbSocketContext();

  const [references, setReferences] = useState<IDataReferences[]>([]);

  const handleGetReference = async () => {
    const start = dayjs(date).startOf('day');
    const end = dayjs(date).endOf('day');
    const search = {
      startTime: start.utc().format(DatePattern.UTC_DATE),
      endTime: end.utc().format(DatePattern.UTC_DATE),
    };

    try {
      const appId = getKeyFromLocalStorage('appId');
      const appKey = getKeyFromLocalStorage('appKey');
      const dbId = getKeyFromLocalStorage('dbId');
      const dbKey = getKeyFromLocalStorage('dbKey');

      const { data } = await getVisits(search, {
        appId: appId ?? '',
        appKey: appKey ?? '',
        dbId: dbId ?? '',
        dbKey: dbKey ?? '',
      });

      setReferences(
        data
          .map((item: IReferenceVisit): IDataReferences => {
            const lastVisit = item.visits[item.visits.length - 1];
            return {
              id: item.id,
              shortId: item.id.slice(-4),
              startTime: lastVisit.startTime,
              endTime: lastVisit.endTime,
              tag: item.tag ? item.tag.split(',') : [],
            };
          })
          .sort((prev: IDataReferences, next: IDataReferences) => {
            const timePrev = dayjs(prev.endTime).valueOf();
            const timeNext = dayjs(next.endTime).valueOf();
            return timeNext - timePrev;
          })
      );
    } catch (error) {
      // empty
    }
  };

  const handleToDetail = (id: string) => {
    return navigate(
      PAGE_LIST.USER_DETAIL.pathname(id) + `?startTime=${date}&endTime=${date}`
    );
  };

  useEffect(() => {
    handleGetReference();
  }, [date, eventId]);

  // Reconnect socket when Home component mounts (in case dbId/dbKey was set from Setting)
  useEffect(() => {
    const dbId = getKeyFromLocalStorage('dbId');
    const dbKey = getKeyFromLocalStorage('dbKey');

    if (dbId && dbKey && isConnected === false) {
      reconnect();
    }
  }, [isConnected]);

  return (
    <Box
      sx={{
        flex: 1,
        height: { xs: 'calc(100dvh - 116px)', sm: 'calc(100dvh - 137px)' },
        mt: { xs: '116px', sm: '137px' },
        backgroundColor: '#F8F9FA',
        padding: '10px',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      {!references?.length ? (
        <Typography
          component='p'
          sx={{
            textAlign: 'center',
            mt: '20%',
          }}
        >
          {ja.common.not_found}
        </Typography>
      ) : (
        <Box
          sx={{
            mb: '5px',
            display: 'grid',
            gridTemplateColumns: '0.8fr 0.8fr 1.2fr 1.2fr 1fr',
            gap: '10px',
            px: '5px',
            backgroundColor: '#fff',
            borderRadius: '4px',
          }}
        >
          {[
            ja.home.person_photo,
            ja.home.control_number,
            ja.home.start_time,
            ja.home.end_time,
            ja.home.tag,
          ].map((title, index) => (
            <Typography
              key={`title-${index}`}
              component='h4'
              sx={{ lineHeight: '32px', fontWeight: '700' }}
            >
              {title}
            </Typography>
          ))}
        </Box>
      )}
      {references.map((ref) => (
        <Card
          key={ref.id}
          fullWidth
          sx={{
            mb: '10px',
            gridTemplateColumns: '0.8fr 0.8fr 1.2fr 1.2fr 1fr',
            backgroundColor: '#fff',
            cursor: 'pointer',
          }}
          onClick={() => handleToDetail(ref.id)}
        >
          <Box display='flex'>
            <SecuredImage
              src={`${API_PATHS.get_reference_image(ref.id)}`}
              alt='face'
              style={{
                width: 48,
                height: 48,
                objectFit: 'cover',
                border: '1px solid #000000',
              }}
            />
          </Box>
          <TypographyCustom>{ref.shortId}</TypographyCustom>
          <TypographyCustom>
            {convertDateUTCToLocal(ref.startTime, DatePattern.TIME)}
          </TypographyCustom>
          <TypographyCustom>
            {convertDateUTCToLocal(ref.endTime, DatePattern.TIME)}
          </TypographyCustom>
          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              overflowX: 'auto',
              alignItems: 'center',
            }}
          >
            {ref.tag.map((tag, index) => (
              <Box
                key={tag + index}
                sx={{
                  padding: '4px 6px',
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '16px',
                }}
              >
                <Typography
                  component='p'
                  sx={{
                    color: '#fff',
                    textAlign: 'center',
                  }}
                >
                  {tag}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>
      ))}
    </Box>
  );
};

export default Home;
